/**
 * mobileBindings.js — Delegated event handlers for mobile (and desktop) buttons
 * Mikhmel Cornerstone4D DICOM Viewer Pro v6
 *
 * [v6.1.0 — QWEN 3.8 PATCHED]:
 *  ✅ FIXED: Защита от повторной инициализации (HMR-safe)
 *  ✅ FIXED: Убрана двойная инициализация AbortController
 *  ✅ FIXED: preventDefault для data-proxy (защита от <a href>)
 *  ✅ NEW: Throttling для input sync через rAF (максимум 60 вызовов/сек)
 *  ✅ NEW: Default case в switch(data-action) + логирование неизвестных
 *  ✅ NEW: Экспорт initMobileBindings для re-init после destroy
 *  ✅ IMPROVED: Graceful handling отсутствующих функций
 */

import { log } from '../utils/logger.js';

// ══════════════════════════════════════════════════════════════
//  STATE
// ══════════════════════════════════════════════════════════════

let _ac = null;
let _initialized = false;
let _inputRafId = null;

// ══════════════════════════════════════════════════════════════
//  HELPERS
// ══════════════════════════════════════════════════════════════

/**
 * Безопасный вызов глобальной функции
 */
function _safeCall(name, ...args) {
  const fn = name.split('.').reduce((o, k) => o && o[k], window);
  if (typeof fn === 'function') {
    try { fn(...args); return true; }
    catch (e) { log.warn(`[mobileBindings] ${name} error:`, e.message); return false; }
  }
  return false;
}

/**
 * Throttled обработчик input через requestAnimationFrame
 * Предотвращает spam при быстром движении слайдера
 */
function _throttledInputSync(e) {
  if (_inputRafId) return;
  _inputRafId = requestAnimationFrame(() => {
    _inputRafId = null;
    _handleInputSync(e);
  });
}

function _handleInputSync(e) {
  const target = e.target;
  if (!target || !target.dataset) return;

  // data-sync="targetId" + data-sync-display="displayId"
  const syncId = target.dataset.sync;
  if (syncId) {
    const targetEl = document.getElementById(syncId);
    if (targetEl) targetEl.value = target.value;

    const displayId = target.dataset.syncDisplay;
    if (displayId) {
      const displayEl = document.getElementById(displayId);
      if (displayEl) displayEl.textContent = target.value;
    }
    return;
  }

  // data-sync-input="targetId" — sync + fire event
  const syncInputId = target.dataset.syncInput;
  if (syncInputId) {
    const syncTarget = document.getElementById(syncInputId);
    if (syncTarget) {
      syncTarget.value = target.value;
      syncTarget.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }
}

// ══════════════════════════════════════════════════════════════
//  CLICK HANDLER
// ══════════════════════════════════════════════════════════════

function _handleClick(e) {
  const target = e.target;
  if (!target || !target.closest) return;

  // ── data-proxy="btn-X" ──
  const btn = target.closest('[data-proxy]');
  if (btn) {
    const targetId = btn.dataset.proxy;
    const targetEl = document.getElementById(targetId);

    // [FIX] preventDefault если это ссылка
    if (btn.tagName === 'A' || btn.closest('a')) {
      e.preventDefault();
    }

    if (targetEl) {
      targetEl.click();
    } else {
      log.warn(`[mobileBindings] Proxy target not found: #${targetId}`);
    }

    if (btn.hasAttribute('data-close')) {
      _safeCall('closeMobileDrawers');
    }
    return;
  }

  // ── data-mpanel="X" ──
  const mpanel = target.closest('[data-mpanel]');
  if (mpanel) {
    const panel = mpanel.dataset.mpanel;
    if (!_safeCall('switchMPanel', panel)) {
      log.warn('[mobileBindings] switchMPanel not available');
    }
    return;
  }

  // ── data-view-mode="X" ──
  const viewMode = target.closest('[data-view-mode]');
  if (viewMode) {
    const mode = viewMode.dataset.viewMode;
    if (window.MikhmelViewer?.activateViewMode) {
      try { window.MikhmelViewer.activateViewMode(mode); }
      catch (err) { log.warn('[mobileBindings] activateViewMode error:', err.message); }
    } else {
      log.warn('[mobileBindings] MikhmelViewer.activateViewMode not available');
    }

    if (viewMode.hasAttribute('data-close')) {
      _safeCall('closeMobileDrawers');
    }
    return;
  }

  // ── data-action="X" ──
  const action = target.closest('[data-action]');
  if (action) {
    const act = action.dataset.action;

    switch (act) {
      case 'profile':
        _safeCall('_showProfileSelector');
        break;

      case 'pwa-install':
        if (window._mobilePWAInstall) {
          try { window._mobilePWAInstall(); }
          catch (err) { log.warn('[mobileBindings] PWA install error:', err.message); }
        } else {
          const pwaBtn = document.getElementById('btn-pwa-install');
          if (pwaBtn) pwaBtn.click();
          else log.warn('[mobileBindings] PWA install not available');
        }
        break;

      // [11.1.21] Кейс 'cinematic-nav' УДАЛЁН: он открывал ящик «Панели»
      // через window.mobileNav('panels') — и подсветку получала кнопка
      // «Панели», хотя нажата была «Кинема» (замечание владельца со
      // скриншотом). Кнопка переведена на data-nav="cinematic": подсветкой и
      // ящиком теперь занимается сам mobileNav, у которого кнопка и ящик
      // связаны одной картой — рассинхрон невозможен по построению.
      case 'overlay-level':
        try { window.MikhmelViewer?.cycleOverlayLevel?.(); }
        catch (err) { log.warn('[mobileBindings] подписи:', err.message); }
        break;

      // [11.0.2] case 'enterprise-nav' УДАЛЁН вместе с пунктом «Разделы»
      // (решение владельца: дубль «Панелей»). Всё, что он делал, — вешал
      // классу `sidebar-open`, для которого нет ни одного правила стилей, и
      // подсвечивал сам себя. Подсветка притом залипала: остальные пункты
      // снимают `active` друг с друга внутри mobileNav(), а этот путь шёл
      // мимо неё.

      case 'close-drawers':
        _safeCall('closeMobileDrawers');
        break;

      // [11.0.2] case 'toggle-sidebar' УДАЛЁН: `data-action="toggle-sidebar"`
      // нет ни на одном элементе разметки (проверено поиском), а сам он
      // дёргал тот же несуществующий класс `sidebar-open`. Дверь без здания.

      default:
        log.debug(`[mobileBindings] Unknown action: ${act}`);
        break;
    }

    if (action.hasAttribute('data-close')) {
      _safeCall('closeMobileDrawers');
    }
    return;
  }
}

// ══════════════════════════════════════════════════════════════
//  INIT & DESTROY
// ══════════════════════════════════════════════════════════════

/**
 * Инициализация event delegation
 * Можно вызывать повторно после destroy (например, при HMR)
 */
export function initMobileBindings() {
  // [FIX] Защита от повторной инициализации
  if (_initialized) {
    log.debug('[mobileBindings] Already initialized, skipping');
    return;
  }

  // [FIX] Создаём AbortController только здесь (убрана двойная инициализация)
  if (_ac) _ac.abort();
  _ac = new AbortController();

  document.addEventListener('click', _handleClick, { signal: _ac.signal });
  document.addEventListener('input', _throttledInputSync, { signal: _ac.signal, passive: true });

  _initialized = true;
  log.debug('[mobileBindings] Initialized (delegated click + throttled input)');
}

/**
 * Полная очистка всех слушателей
 */
export function destroyMobileBindings() {
  if (_ac) {
    _ac.abort();
    _ac = null;
  }

  if (_inputRafId) {
    cancelAnimationFrame(_inputRafId);
    _inputRafId = null;
  }

  _initialized = false;
  log.debug('[mobileBindings] Destroyed');
}

/**
 * Получить состояние модуля (для диагностики)
 */
export function getMobileBindingsState() {
  return {
    initialized: _initialized,
    hasAbortController: !!_ac,
    hasPendingInput: !!_inputRafId,
  };
}

// Auto-init при первом импорте модуля
// [NOTE] Если модуль импортируется динамически и нужна отложенная инициализация,
//        уберите эту строку и вызывайте initMobileBindings() явно из main.js
initMobileBindings();